import React, { useState, useEffect, useRef, useMemo } from "react";
import { Plus, RefreshCw, Search, X } from "lucide-react";
import UserTableCRM from "./UserTableCRM";
import AddUserWizard from "./AddUserWizard";
import apiClient from "../../../utils/apiClient";
import server from "../../../utils/serverConfig";
import toast from "react-hot-toast";

// Import existing edit modals
import { EditFyersUserModal } from "../../BotConfigs/FyersUsersList/EditFyersUserModal";
import { EditNubraUserModal } from "../../BotConfigs/FyersUsersList/EditNubraUserModal";
import { EditDhanXTSUserModal } from "../../BotConfigs/FyersUsersList/EditDhanXTSUserModal";
import { EditDhanHQUserModal } from "../../BotConfigs/FyersUsersList/EditDhanHQUserModal";
import { EditZerodhaUserModal } from "../../BotConfigs/FyersUsersList/EditZerodhaUserModal";
import { EditMOSLUserModal } from "../../BotConfigs/FyersUsersList/EditMOSLUserModal";
import apiClientChoice from "../../../utils/apiClientChoice";

const BROKERS = [
  { id: "fyers", label: "Fyers", color: "bg-blue-600", badge: "FY" },
  { id: "nubra", label: "Nubra", color: "bg-purple-600", badge: "NU" },
  { id: "dhan_hq", label: "DhanHQ", color: "bg-emerald-700", badge: "DHQ" },
  { id: "zerodha", label: "Zerodha", color: "bg-red-700", badge: "ZE" },
  { id: "mosl", label: "MOSL", color: "bg-amber-600", badge: "MOS" }
];

export default function UsersOnboarding() {
  const [activeBroker, setActiveBroker] = useState("fyers");
  const [showAddWizard, setShowAddWizard] = useState(false);
  const [proxyData, setProxyData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const proxyDataRef = useRef(null);
  const tableScrollRef = useRef(null);

  // Separate states to avoid race conditions
  const [localUsers, setLocalUsers] = useState({
    fyers: [],
    nubra: [],
    dhan_hq: [],
  });

  const [choiceUsers, setChoiceUsers] = useState({
    zerodha: [],
    mosl: [],
    dhan_hq: [],
  });

  // Derived combined users object
  const usersByBroker = useMemo(() => {
    const mergedDhanHQ = [...localUsers.dhan_hq];
    choiceUsers.dhan_hq.forEach((cu) => {
      if (!mergedDhanHQ.some((lu) => String(lu.client_id) === String(cu.client_id))) {
        mergedDhanHQ.push(cu);
      }
    });

    return {
      fyers: localUsers.fyers,
      nubra: localUsers.nubra,
      dhan_hq: mergedDhanHQ,
      zerodha: choiceUsers.zerodha,
      mosl: choiceUsers.mosl,
    };
  }, [localUsers, choiceUsers]);

  const fetchAllUsers = async () => {
    // Preserve scroll position so the list doesn't jump after re-render
    const scrollEl = tableScrollRef.current;
    const savedScrollTop = scrollEl ? scrollEl.scrollTop : 0;
    try {
      const response = await apiClient.get(`${server.godseye.baseUrl}get-fyers-users`);
      if (response.data) {
        const grouped = {
          fyers: [],
          nubra: [],
          dhan_hq: [],
        };

        response.data.forEach((u) => {
          const broker = String(u.broker || "").toLowerCase();
          const brokerKey = (broker === "dhan_hq" || broker === "dhanhq") ? "dhan_hq" : broker;
          if (grouped[brokerKey]) {
            grouped[brokerKey].push(u);
          }
        });

        setLocalUsers({ fyers: grouped.fyers, nubra: grouped.nubra, dhan_hq: grouped.dhan_hq });

        // Restore scroll after React flushes the DOM update
        requestAnimationFrame(() => {
          if (scrollEl) scrollEl.scrollTop = savedScrollTop;
        });
      }
    } catch (err) {
      console.error("Failed to fetch onboarded users:", err);
    }
  };

  const fetchChoiceUsers = async () => {
    try {
      const response = await apiClientChoice.get("https://choice.server.sypto.xyz/users");
      const allUsers = response.data || [];
      const normalizeStatus = (u) => ({ ...u, status: (u.account_status || "").toLowerCase() });
      const zerodhaUsers = allUsers.filter((u) => u.broker === "ZERODHA").map((u) => normalizeStatus({ ...u, broker: "zerodha" }));
      const moslUsers = allUsers.filter((u) => u.broker === "MOSL").map((u) => normalizeStatus({ ...u, broker: "mosl" }));
      const choiceDhanHQUsers = allUsers.filter((u) => u.broker === "DHANHQ").map((u) => normalizeStatus({ ...u, broker: "dhan_hq", isChoiceUser: true }));
      
      setChoiceUsers({
        zerodha: zerodhaUsers,
        mosl: moslUsers,
        dhan_hq: choiceDhanHQUsers,
      });
    } catch (err) {
      console.error("Failed to fetch Choice users:", err);
    }
  };

  useEffect(() => {
    fetchAllUsers();
    fetchChoiceUsers();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchAllUsers(), fetchChoiceUsers()]);
    setIsRefreshing(false);
    toast.success("Users refreshed");
  };

  // ── Proxy management ────────────────────────────────────────────────────────
  const claimProxy = async () => {
    if (proxyDataRef.current) return;
    try {
      const response = await apiClient.request({
        method: "get",
        url: `${server.godseye.baseUrl}proxies/next`,
      });
      if (response.data) {
        setProxyData(response.data);
        proxyDataRef.current = response.data;
      }
    } catch (error) {
      console.error("Failed to claim proxy:", error);
    }
  };

  const releaseProxy = async () => {
    const current = proxyDataRef.current;
    if (current) {
      try {
        await apiClient.request({
          method: "post",
          url: `${server.godseye.baseUrl}proxies/release`,
          data: { proxy_static_ip: current.proxy_static_ip },
        });
      } catch (err) {
        console.error("Failed to release proxy:", err);
      }
      setProxyData(null);
      proxyDataRef.current = null;
    }
  };

  const handleOpenWizard = async () => {
    await claimProxy();
    setShowAddWizard(true);
  };

  const handleCloseWizard = async (success) => {
    const current = proxyDataRef.current;
    if (success && current) {
      try {
        await apiClient.request({
          method: "post",
          url: `${server.godseye.baseUrl}proxies/commit`,
          data: { proxy_static_ip: current.proxy_static_ip },
        });
      } catch (err) {
        console.error("Failed to commit proxy:", err);
      }
      setProxyData(null);
      proxyDataRef.current = null;
    } else {
      await releaseProxy();
    }
    setShowAddWizard(false);
    setSearchQuery("");
    if (success) {
      fetchAllUsers();
      fetchChoiceUsers();
    }
  };

  // Proxy heartbeat
  useEffect(() => {
    if (!proxyData) return;
    const interval = setInterval(async () => {
      try {
        await apiClient.request({
          method: "post",
          url: `${server.godseye.baseUrl}proxies/heartbeat`,
          data: { proxy_static_ip: proxyData.proxy_static_ip },
        });
      } catch (err) {
        console.error("Heartbeat failed", err);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [proxyData]);

  // ── Broker add-user handlers ─────────────────────────────────────────────────
  const handleAddFyers = (client_id, name) => {
    fetchAllUsers();
  };
  const handleAddNubra = (client_id, name) => {
    fetchAllUsers();
  };
  const handleAddZerodha = (client_id, name) => {
    fetchAllUsers();
  };
  const handleAddDhanHQ = (client_id, name) => {
    fetchAllUsers();
  };
  const handleAddMOSL = (client_id, name) => {
    fetchAllUsers();
  };

  // ── Row actions ──────────────────────────────────────────────────────────────
  const handleEdit = (user) => {
    // Reset first so the modal always unmounts + remounts even for the same user
    setEditingUser(null);
    setTimeout(() => setEditingUser(user), 0);
  };

  // ── Filtered users for current broker tab ────────────────────────────────────
  const currentUsers = (usersByBroker[activeBroker] || [])
    .filter((u) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.client_id || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const idA = (a.client_id || "").toLowerCase();
      const idB = (b.client_id || "").toLowerCase();
      if (idA < idB) return -1;
      if (idA > idB) return 1;
      return 0;
    });

  const activeBrokerInfo = BROKERS.find((b) => b.id === activeBroker);

  return (
    <div className="flex flex-col h-full">

      {/* ── Top bar: heading + global Add User button ── */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-zinc-800/60">
        <div>
          <p className="text-white text-sm font-semibold">Onboarded Users</p>
          <p className="text-zinc-500 text-xs mt-0.5">
            {Object.values(usersByBroker).flat().length} total across all brokers
          </p>
        </div>

        {/* ── Global Add User ── */}
        <button
          onClick={handleOpenWizard}
          id="crm-add-user-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 active:scale-95 text-white text-sm font-semibold transition-all duration-200 shadow-lg shadow-blue-600/25"
        >
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {/* ── Broker tabs ── */}
      <div className="flex items-center gap-1 px-4 pt-3 pb-0 border-b border-zinc-800/60">
        {BROKERS.map((broker) => (
          <button
            key={broker.id}
            onClick={() => setActiveBroker(broker.id)}
            className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all duration-200 ${activeBroker === broker.id
                ? "text-white bg-zinc-800/80 border-t border-l border-r border-zinc-700/60 -mb-px"
                : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30"
              }`}
          >
            <span
              className={`w-5 h-5 rounded text-[9px] font-bold flex items-center justify-center ${broker.color} text-white`}
            >
              {broker.badge}
            </span>
            {broker.label}
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-zinc-700/60 text-zinc-400 text-[10px]">
              {(usersByBroker[broker.id] || []).length}
            </span>
          </button>
        ))}
      </div>

      {/* ── Toolbar (search + refresh) ── */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-800/40 bg-zinc-900/20">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${activeBrokerInfo?.label} users…`}
            autoComplete="off"
            className="bg-zinc-800/60 border border-zinc-700/50 rounded-lg pl-8 pr-8 py-2 text-white text-xs placeholder-zinc-600 focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/20 w-full transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Refresh */}
        <button
          onClick={handleRefresh}
          className="p-2 rounded-lg bg-zinc-800/60 border border-zinc-700/50 text-zinc-400 hover:text-white hover:bg-zinc-700/60 transition-all"
          title="Refresh users"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* ── User Table ── */}
      <div ref={tableScrollRef} className="flex-1 overflow-auto sleek-scrollbar">
        <UserTableCRM
          users={currentUsers}
          broker={activeBrokerInfo?.label}
          onEdit={handleEdit}
        />
      </div>

      <AddUserWizard
        isOpen={showAddWizard}
        onClose={handleCloseWizard}
        proxyData={proxyData}
        handleAddFyers={handleAddFyers}
        handleAddNubra={handleAddNubra}
        handleAddDhanHQ={handleAddDhanHQ}
        handleAddZerodha={handleAddZerodha}
        handleAddMOSL={handleAddMOSL}
      />

      {/* ── Edit User Modal (On-demand) ── */}
      {editingUser && (
        <>
          {editingUser.broker === "fyers" && (
            <EditFyersUserModal
              userDetails={editingUser}
              fetchFyersUserList={fetchAllUsers}
              forceOpen={true}
              onForceClose={() => { setEditingUser(null); }}
            />
          )}
          {editingUser.broker === "nubra" && (
            <EditNubraUserModal
              userDetails={editingUser}
              fetchFyersUserList={fetchAllUsers}
              forceOpen={true}
              onForceClose={() => { setEditingUser(null); }}
              isPolarisOrPolarisLite={!!(editingUser.product_is_polaris || editingUser.product_is_polaris_lite)}
            />
          )}
          {editingUser.broker === "dhan_hq" && (
            <EditDhanHQUserModal
              userDetails={editingUser}
              fetchFyersUserList={editingUser.isChoiceUser ? fetchChoiceUsers : fetchAllUsers}
              forceOpen={true}
              onForceClose={() => { setEditingUser(null); }}
              isPolarisOrPolarisLite={editingUser.isChoiceUser || !!(editingUser.product_is_polaris || editingUser.product_is_polaris_lite)}
            />
          )}
          {(editingUser.broker === "dhan_xts" || editingUser.broker === "dhan_xts_anurag") && (
            <EditDhanXTSUserModal
              userDetails={editingUser}
              fetchFyersUserList={fetchAllUsers}
              forceOpen={true}
              onForceClose={() => { setEditingUser(null); }}
            />
          )}
          {editingUser.broker === "zerodha" && (
            <EditZerodhaUserModal
              userDetails={editingUser}
              fetchUserList={fetchChoiceUsers}
              forceOpen={true}
              onForceClose={() => { setEditingUser(null); }}
            />
          )}
          {editingUser.broker === "mosl" && (
            <EditMOSLUserModal
              userDetails={editingUser}
              fetchUserList={fetchChoiceUsers}
              forceOpen={true}
              onForceClose={() => { setEditingUser(null); }}
            />
          )}
          {!["fyers", "nubra", "dhan_hq", "dhan_xts", "dhan_xts_anurag", "zerodha", "mosl"].includes(editingUser.broker) && (
            (() => {
              toast(`Edit for ${editingUser.broker} coming soon!`);
              setEditingUser(null);
              return null;
            })()
          )}
        </>
      )}
    </div>
  );
}
